/**
 * Decide whether a saved/typed answer should count as "not yet provided".
 *
 * Mirrors the backend `isEmptyAnswer` in
 * Portal-Backend/services/formReadiness.service.js so the frontend's
 * per-section validator and the backend's submit-readiness gate agree on
 * what counts as an answer. If you change behaviour here, change both.
 *
 * Rules:
 *   - undefined / null               -> empty
 *   - string                         -> empty when trimmed length is 0
 *   - array                          -> empty when every element is empty
 *   - plain object (composite, etc.) -> empty when every sub-field is empty
 *                                       (matches the "all sub-fields blank"
 *                                       semantic so partially-typed
 *                                       composites are NOT penalised)
 *   - everything else (number, bool) -> non-empty (0 / false are valid
 *                                       answers)
 */
export function isEmptyAnswer(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.every(isEmptyAnswer);
  if (typeof value === "object") {
    const values = Object.values(value as Record<string, unknown>);
    if (values.length === 0) return true;
    return values.every(isEmptyAnswer);
  }
  return false;
}
